import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import cx from "classnames";
import styles from "./styles.module.sass";
import { hostname } from "../../constants";
import Page from "../../layouts/Page";
import Button from "../../components/Button";

/**
 * WordPress REST helpers
 */
type WPMedia = { source_url?: string; alt_text?: string };
type WPFile = { url?: string; title?: string };
type WPEventsACF = {
  events_image?: { url?: string; alt?: string } | string;
  events_kit_pdf?: { url?: string; title?: string } | string;
};
type WPPage = {
  id: number;
  slug: string;
  title?: { rendered?: string };
  acf?: WPEventsACF;
  _embedded?: {
    "wp:featuredmedia"?: Array<WPMedia>;
  };
};

const COUNTRY_EMAIL: Record<string, { to: string; subject: string }> = {
  singapore: {
    to: "mail@lloydsinn.com",
    subject: "Lloyd’s Inn SG Event Enquiry",
  },
  bali: {
    to: "sales@lloydsinn.com",
    subject: "Lloyd’s Inn Bali Event Enquiry",
  },
  "kuala-lumpur": {
    to: "sales.kl@lloydsinn.com",
    subject: "Lloyd’s Inn KL Event Enquiry",
  },
  suites: {
    to: "",
    subject: "Lloyd’s Inn Bali Suites Event Enquiry",
  },
};

function countryLabel(slug: string): string {
  switch (slug) {
    case "bali":
      return "Bali";
    case "kuala-lumpur":
      return "Kuala Lumpur";
    case "suites":
      return "Suites";
    case "singapore":
    default:
      return "Singapore";
  }
}

function buildGmailComposeURL(to: string, subject: string, body?: string) {
  const params = new URLSearchParams();
  params.set("view", "cm");
  params.set("fs", "1");
  params.set("to", to);
  params.set("su", subject);
  if (body) params.set("body", body);
  return `https://mail.google.com/mail/?${params.toString()}`;
}

function normalizeImage(
  events: WPEventsACF["events_image"],
  embedded?: WPMedia[],
): { url?: string; alt?: string } {
  if (!events) {
    if (embedded && embedded[0]?.source_url) {
      return {
        url: embedded[0].source_url,
        alt: embedded[0].alt_text || "Events",
      };
    }
    return {};
  }
  if (typeof events === "string") return { url: events, alt: "Events" };
  return { url: events.url, alt: events.alt || "Events" };
}

function normalizeFile(f: WPEventsACF["events_kit_pdf"]): {
  url?: string;
  title?: string;
} {
  if (!f) return {};
  if (typeof f === "string") return { url: f, title: "Event Kit" };
  return { url: f.url, title: f.title || "Event Kit" };
}

export default function EventsPage() {
  const params = useParams<{ country?: string }>();
  const country = useMemo(
    () => (params.country || "singapore").toLowerCase(),
    [params.country],
  );

  const page: Record<string, { pageNo: string }> = {
    singapore: { pageNo: "370" },
    bali: { pageNo: "353" },
    "kuala-lumpur": { pageNo: "435" },
    suites: { pageNo: "353" },
  };

  const [loading, setLoading] = useState(true);
  const [wp, setWP] = useState<WPPage | null>(null);

  const emailInfo = COUNTRY_EMAIL[country] || COUNTRY_EMAIL.singapore;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetch(
      `${hostname}/${country}/wp-json/wp/v2/pages/${page[country].pageNo}?_embed`,
    )
      .then((res) => res.json())
      .then((pageJson: any) => {
        if (mounted) setWP(pageJson as WPPage);
      })
      .catch(console.error)
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [country]);

  const events = normalizeImage(
    wp?.acf?.events_image,
    wp?._embedded?.["wp:featuredmedia"],
  );
  const eventKit = normalizeFile(wp?.acf?.events_kit_pdf);

  const onEventKit = () => {
    if (eventKit.url)
      window.open(eventKit.url, "_blank", "noopener,noreferrer");
  };

  const onEnquiry = () => {
    // Pre-filled form content (body) for Gmail compose
    const bodyLines = [
      "First Name:",
      "Last Name:",
      "Email:",
      "Contact Number:",
      `Country:`,
      "Event Type: Brand collaboration / Hotel Takeover / Birthday celebration / Team meetings / Retreats / Product Launch / Others",
      "No. of Pax:",
      "Event Date:",
      "What is your budget?",
      "Full Day / Half Day:",
      "Special Requests:",
    ];
    const body = bodyLines.join("\n");

    const url = buildGmailComposeURL(emailInfo.to, emailInfo.subject, body);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // ❌ remove dark filter: use only the image (no linear-gradient)
  const heroBackground = events.url
    ? {
        backgroundImage: `url("${events.url}")`,
      }
    : undefined;

  return (
    <Page type="local" country={country}>
      <div className={cx(styles.EventsPage)}>
        {loading ? (
          <div className={styles.loading}>Loading…</div>
        ) : (
          <section className={styles.eventsWrap}>
            <div className={styles.eventsBox} style={heroBackground}>
              {!events.url && (
                <div className={styles.eventsPlaceholder}>
                  Upload Events image in WordPress (ACF:{" "}
                  <strong>events_image</strong>)
                </div>
              )}

              {/* Centered CTAs with equal size */}
              <div className={styles.overlayControls}>
                <Button
                  buttonVariant="frameless"
                  clickHandler={onEnquiry}
                  className={styles.overlayBtn}
                >
                  ENQUIRE NOW
                </Button>
                <Button
                  buttonVariant="frameless"
                  clickHandler={onEventKit}
                  disabled={!eventKit.url}
                  className={styles.overlayBtn}
                >
                  EVENT KIT
                </Button>
              </div>
            </div>
          </section>
        )}
      </div>
    </Page>
  );
}
